import {EstimateTreeDto, TreeMaterialSpec, WorkItemType} from '../../../shared/dtos/estimate-tree.dto';
import {TreeOperationType} from '../../../shared/dtos/estimate-tree-operation.dto';
import {EstimateTreeEquipmentDto} from '../../../shared/dtos/estimate-tree-equipment.dto';
import {EquipmentDto} from '../../equipment/dtos/equipment.dto';

/*
 * The Work Plan's model (WorkPlan.kt, SQ-486): which sections a work item's
 * operations bring in, where each answer is stored (the tree's
 * additionalDetails, one key per answer, the chip label as the value), and
 * how an answer reads once its section closes. The four keys that predate it
 * (removalStrategy, reachMethod, debrisMethod, riggingAmount) keep their names
 * and values: the server and the similarity service read them.
 */

const T = TreeOperationType;

export const CLEAN_HAUL = 'Clean/Haul Everything Away';
export const LEAVE_EVERYTHING = 'Leave Everything';
export const LEAVE_BRUSH_HAUL_WOOD = 'Leave Brush - Haul Wood Off';
export const LEAVE_WOOD_HAUL_BRUSH = 'Leave Wood - Haul Brush Off';
export const brushWoodOptions = (misc: boolean) =>
  misc ? [CLEAN_HAUL, LEAVE_EVERYTHING] : [CLEAN_HAUL, LEAVE_EVERYTHING, LEAVE_BRUSH_HAUL_WOOD, LEAVE_WOOD_HAUL_BRUSH];

export const ANY_LENGTH = 'Any Length';
export const CUT_TO_LENGTH = 'Cut to Length';
export const WHERE_IT_FALLS = 'Where It Falls';
export const SPECIFIC_LOCATION = 'Specific Location';
export const HAULED_AWAY = 'Hauled Away';

export const CLEANUP_OPTIONS = [
  'Leave All Grindings Where They Fall', 'Fill Hole & Leave Excess on Grind Area', 'Fill Hole & Remove Excess', 'Remove All Grindings & Leave Hole Open',
];
/** The two answers that remove material ask where it goes. */
export const CLEANUP_FOLLOW_UP: Record<string, string> = {
  'Fill Hole & Remove Excess': 'Where The Excess Goes',
  'Remove All Grindings & Leave Hole Open': 'Where The Grindings Go',
};

export const FELL_WHOLE_TREE = 'Fell Whole Tree';
export const REMOVAL_OPTIONS = [FELL_WHOLE_TREE, 'Removed Piece by Piece'];
export const REACH_OPTIONS = ['Climbing', 'Done from the Ground'];
export const DROPPING_OPTIONS = ['Freely Fall', 'Carefully Dropped'];
export const RIGGING_OPTIONS = ['A Little', 'Quite a Bit', 'A Lot'];
export const REMAINING_STEM_OPTIONS = ['Fell Stem', 'Block/Chunk Down Stem'];
export const LOADER_MATERIAL_OPTIONS = ['Brush Only', 'Wood Only', 'Brush & Wood'];
// SQ-644 (WorkPlan.kt): a loader on a stump job hauls grindings, stump wood or the whole stump. Which are offered, and
// what Stump Wood is called, follows the Cleanup answer; Stump Wood is one stored answer under both labels.
export const GRINDINGS = 'Grindings';
export const STUMP_WOOD = 'Stump Wood';
export const WHOLE_STUMP = 'Whole Stump';
export const STUMP_LOADER_MATERIAL_OPTIONS = [GRINDINGS, STUMP_WOOD, WHOLE_STUMP];
export const LOADER_ACCESS_OPTIONS = ['Full Access', 'Partway Access'];

export const riggingAnswer = (v: string) => v === 'A Little' ? 'A Little Rigging' : `${v} of Rigging`;
export const droppingAnswer = (v: string) => `Debris ${v}`;
export const loaderMaterialAnswer = (v: string) => `Hauling ${v}`;
export const loaderAccessAnswer = (v: string) => `${v} with Machine`;

/** The areas' titles on a work item: "Current" and "Target" (SQ-487 §8f). */
export const AREA_LABELS: Record<string, string> = {currentClearanceFeet: 'Current', targetClearanceFeet: 'Target', reductionFeet: 'Reduction Amount'};

// ---- Which sections ---------------------------------------------------------------------------

const BRUSH_WOOD_OPS: string[] = [T.Tree_Removal, T.Thinning, T.Custom_Trim_Prune, T.Ground_To_Canopy_Clearance, T.Crown_Reduction, T.Debris_Removal, T.Misc_Task];
const TREE_WORK_OPS: string[] = [T.Tree_Removal, T.Thinning, T.Custom_Trim_Prune, T.Ground_To_Canopy_Clearance, T.Crown_Reduction];
const STUMP_OPS: string[] = [T.Stump_Grinding, T.Whole_Stump_Removal];

/** The sections are the union across every operation on the item, not its type. */
export const planSections = (tree: EstimateTreeDto) => {
  const types = tree.operations.map(o => o.operationType as string);
  const removal = types.includes(T.Tree_Removal);
  const fellWhole = (tree.additionalDetails as Record<string, unknown>)?.removalStrategy === FELL_WHOLE_TREE;
  return {
    any: types.length > 0,
    cleanup: types.some(t => STUMP_OPS.includes(t)),
    brushWood: types.some(t => BRUSH_WOOD_OPS.includes(t)),
    misc: types.includes(T.Misc_Task),
    removal,
    /** Reach, Dropping and Rigging; never once the whole tree is felled. */
    treeWork: types.some(t => TREE_WORK_OPS.includes(t)) && !fellWhole,
    remainingStem: removal,
  };
};

/**
 * A stump work item, by its type or the stump operations on it (WorkPlan.isStumpWork). The record page and the library
 * page both ask this, so a Tree carrying a stump operation is a stump on both.
 */
export const isStumpWorkOf = (workItemType: string | undefined, operationTypes: string[]) =>
  workItemType === WorkItemType.Stump || operationTypes.some(t => STUMP_OPS.includes(t));
export const isStumpWork = (tree: EstimateTreeDto) =>
  isStumpWorkOf(tree.workItemType, tree.operations.map(o => o.operationType as string));

/** The Cleanup answers that leave the grindings on site, and the two that take them away. */
const [LEAVE_ALL_GRINDINGS, FILL_LEAVE_EXCESS, FILL_REMOVE_EXCESS, REMOVE_ALL_GRINDINGS] = CLEANUP_OPTIONS;
export const GRINDINGS_LEFT = [LEAVE_ALL_GRINDINGS, FILL_LEAVE_EXCESS];
export const GRINDINGS_REMOVED = [FILL_REMOVE_EXCESS, REMOVE_ALL_GRINDINGS];
const cleanupOf = (tree: EstimateTreeDto) => String((tree.additionalDetails as Record<string, unknown>)?.cleanup ?? '');

/** The loader's hauling answers on this item; `cleanup` stands in for the stored one while it changes. */
export const loaderMaterialOptions = (tree: EstimateTreeDto, cleanup = cleanupOf(tree)) =>
  !isStumpWork(tree) ? LOADER_MATERIAL_OPTIONS
    : GRINDINGS_LEFT.includes(cleanup) ? [STUMP_WOOD, WHOLE_STUMP]
      : STUMP_LOADER_MATERIAL_OPTIONS;

/** A hauling answer as its chip reads here: Stump Wood is Wood & Grindings while the grindings go too. */
export const loaderMaterialLabel = (tree: EstimateTreeDto, option: string) =>
  option === STUMP_WOOD && isStumpWork(tree) && GRINDINGS_REMOVED.includes(cleanupOf(tree)) ? 'Wood & Grindings' : option;

/**
 * The detail patch a Cleanup pick makes: the answer, plus clearing a hauling answer the change narrowed out (offered
 * before it, not after: Grindings once the grindings stay). An answer the old Cleanup did not offer either (a Brush /
 * Wood answer saved on a stump before SQ-644) is not this change's to clear, and stays.
 */
export const cleanupPatch = (tree: EstimateTreeDto, cleanup: string): Record<string, string> => {
  const material = String((tree.additionalDetails as Record<string, unknown>)?.loaderMaterial ?? '');
  const narrowedOut = material !== '' && loaderMaterialOptions(tree).includes(material)
    && !loaderMaterialOptions(tree, cleanup).includes(material);
  return narrowedOut ? {cleanup, loaderMaterial: ''} : {cleanup};
};

// ---- Brush and Wood ---------------------------------------------------------------------------

export const EMPTY_SPEC: TreeMaterialSpec = {diameter: 0, lengthMode: '', lengthFt: 0, lengthIn: 0, location: '', locationNote: ''};

export const specOf = (tree: EstimateTreeDto, key: 'woodSpec' | 'brushSpec'): TreeMaterialSpec => {
  const raw = (tree.additionalDetails as Record<string, unknown>)?.[key] as Partial<TreeMaterialSpec> | undefined;
  return {...EMPTY_SPEC, ...(raw && typeof raw === 'object' ? raw : {})};
};

const formatLength = (ft = 0, inches = 0) =>
  [ft > 0 && `${ft} ft`, inches > 0 && `${inches} in`].filter(Boolean).join(' ') || '0 ft';

export const specSummary = (spec: TreeMaterialSpec, withDiameter: boolean) => {
  const parts: string[] = [];
  if (withDiameter) parts.push(`${spec.diameter} in and bigger`);
  if (spec.lengthMode === ANY_LENGTH) parts.push('any length');
  if (spec.lengthMode === CUT_TO_LENGTH) parts.push(`cut to ${formatLength(spec.lengthFt, spec.lengthIn)}`);
  if (spec.location === WHERE_IT_FALLS) parts.push('left where it falls');
  if (spec.location === SPECIFIC_LOCATION) parts.push(spec.locationNote.trim() || '(location not specified)');
  return parts.join(', ');
};

const locationSummary = (spec: TreeMaterialSpec) =>
  spec.location === WHERE_IT_FALLS ? 'left where it falls'
    : spec.location === SPECIFIC_LOCATION ? (spec.locationNote.trim() || '(location not specified)') : undefined;

export const brushWoodLines = (answer: string, haul: number, wood: TreeMaterialSpec, brush: TreeMaterialSpec, misc: boolean): string[] => {
  switch (answer) {
    case LEAVE_EVERYTHING:
      if (misc) return [locationSummary(wood)].filter((l): l is string => !!l);
      return [`Wood: ${specSummary(wood, false)}`, `Brush: ${specSummary(brush, false)}`];
    case LEAVE_BRUSH_HAUL_WOOD: return [`Wood: hauled off, ${haul} in and bigger`, `Brush: ${specSummary(brush, false)}`];
    case LEAVE_WOOD_HAUL_BRUSH: return [`Wood: ${specSummary(wood, true)}`, 'Brush: hauled off'];
    default: return [];
  }
};

/** "Cut to Length" counts once it measures above zero, "Specific Location" once the note has text. */
export const specDone = (s: TreeMaterialSpec) => !!s.lengthMode && !!s.location
  && (s.lengthMode !== CUT_TO_LENGTH || s.lengthFt + s.lengthIn > 0)
  && (s.location !== SPECIFIC_LOCATION || !!s.locationNote.trim());
const locationDone = (s: TreeMaterialSpec) => !!s.location && (s.location !== SPECIFIC_LOCATION || !!s.locationNote.trim());

export const brushWoodComplete = (answer: string, wood: TreeMaterialSpec, brush: TreeMaterialSpec, misc: boolean) => {
  switch (answer) {
    case CLEAN_HAUL: return true;
    case LEAVE_EVERYTHING: return misc ? locationDone(wood) : specDone(wood) && specDone(brush);
    case LEAVE_BRUSH_HAUL_WOOD: return specDone(brush);
    case LEAVE_WOOD_HAUL_BRUSH: return specDone(wood);
    default: return false;
  }
};

export const cleanupComplete = (cleanup: string, location: string, note: string) =>
  !!cleanup && (!CLEANUP_FOLLOW_UP[cleanup] || (!!location && (location !== SPECIFIC_LOCATION || !!note.trim())));

export const cleanupDetail = (location: string, note: string) =>
  !location ? undefined : location === SPECIFIC_LOCATION ? (note.trim() || '(location not specified)') : 'hauled away';

// ---- What the customer sees (SQ-337) -----------------------------------------------------------

/** The one Work Plan section on the customer's copy: its title, the answer and the answer's lines. */
export interface CustomerPlan {
  title: string;
  answer: string;
  details: string[];
}

const answerOf = (tree: EstimateTreeDto, key: string) => {
  const v = (tree.additionalDetails as Record<string, unknown> | undefined)?.[key];
  return typeof v === 'string' || typeof v === 'number' ? String(v).trim() : '';
};

/**
 * The customer's Work Plan: only its first section, and only once answered.
 * A stump is Cleanup (where the grindings or the excess go); everything else
 * is Brush and Wood with what is left on site, called Cleanup on a Misc.
 * item. Removal Plan, Reach, Dropping, Rigging, Remaining Stem and the
 * equipment are the crew's, never the customer's. A Tree carrying only a
 * stump operation answers Cleanup as a stump does.
 */
export const customerPlan = (tree: EstimateTreeDto): CustomerPlan | null => {
  const s = planSections(tree);
  const stumpFirst = s.cleanup && (tree.workItemType === WorkItemType.Stump || !s.brushWood);
  if (stumpFirst) {
    const answer = answerOf(tree, 'cleanup');
    if (!answer) return null;
    // Where it goes only for the answers that ask (the catalog's followsAnswers): a location left from an earlier answer never shows.
    const line = CLEANUP_FOLLOW_UP[answer] ? cleanupDetail(answerOf(tree, 'cleanupLocation'), answerOf(tree, 'cleanupNote')) : undefined;
    return {title: 'Cleanup', answer, details: line ? [line] : []};
  }
  if (!s.brushWood) return null;
  const answer = answerOf(tree, 'brushWood');
  if (!answer) return null;
  return {
    title: s.misc ? 'Cleanup' : 'Brush and Wood',
    answer,
    details: brushWoodLines(answer, Number(answerOf(tree, 'haulWoodDiameter') || 0), specOf(tree, 'woodSpec'), specOf(tree, 'brushSpec'), s.misc),
  };
};

// ---- Equipment ---------------------------------------------------------------------------------

/** Selection order for the equipment ("e12,e7"): what "picked" means, and the order machines are listed. */
export const orderKeys = (stored: string) => stored.split(',').map(s => s.trim()).filter(Boolean);
export const appendKey = (stored: string, key: string) => [...orderKeys(stored).filter(k => k !== key), key].join(',');
export const removeKey = (stored: string, key: string) => orderKeys(stored).filter(k => k !== key).join(',');

const storedOrder = (tree: EstimateTreeDto) => {
  const v = (tree.additionalDetails as Record<string, unknown>)?.workPlanEquipment;
  return typeof v === 'string' ? v : '';
};

/** The machines on the item in the order they were picked; rows from before that order come after. */
export const equipmentOrder = (tree: EstimateTreeDto, rows: EstimateTreeEquipmentDto[]) => {
  const order = orderKeys(storedOrder(tree));
  const at = (r: EstimateTreeEquipmentDto) => {
    const i = order.indexOf(`e${r.equipmentId}`);
    return i < 0 ? Number.MAX_SAFE_INTEGER : i;
  };
  return [...rows].sort((a, b) => at(a) - at(b) || a.id - b.id);
};

export const machineName = (e: EquipmentDto) =>
  e.nickname?.trim() || e.type?.trim() || [e.brand, e.model].filter(Boolean).join(' ').trim() || 'Equipment';

/** The loader's two questions hang off its kind: the catalog's Loaders category, else the type, else the name. */
export const isCompactLoader = (machine: EquipmentDto | undefined, row?: EstimateTreeEquipmentDto) => {
  if (machine?.category?.toLowerCase() === 'loaders') return true;
  const type = (machine?.type ?? row?.equipmentType ?? '').trim().toLowerCase();
  const name = (machine ? machineName(machine) : row?.equipmentNickname ?? '').trim().toLowerCase();
  return type === 'compact loader' || name === 'compact loader';
};

export interface PlanMachine {
  key: string;
  machine: EquipmentDto;
  row?: EstimateTreeEquipmentDto;
  picked: boolean;
}

/**
 * The machines the picker offers (WorkPlanBlock.kt planEquipmentItems): the
 * ones set to Show in estimates, plus any already on this work item; picked
 * means in the selection order, or on the item from before that order.
 */
export const planMachines = (tree: EstimateTreeDto, company: EquipmentDto[], rows: EstimateTreeEquipmentDto[]): PlanMachine[] => {
  const order = orderKeys(storedOrder(tree));
  return company
    .filter(m => m.showInEstimates || rows.some(r => r.equipmentId === m.id) || order.includes(`e${m.id}`))
    .map(m => {
      const row = rows.find(r => r.equipmentId === m.id);
      return {key: `e${m.id}`, machine: m, row, picked: order.includes(`e${m.id}`) || !!row};
    });
};

export const pickedInOrder = (tree: EstimateTreeDto, items: PlanMachine[]) => {
  const order = orderKeys(storedOrder(tree));
  const at = (i: PlanMachine) => {
    const n = order.indexOf(i.key);
    return n < 0 ? Number.MAX_SAFE_INTEGER : n;
  };
  return items.filter(i => i.picked).sort((a, b) => at(a) - at(b));
};

export {storedOrder};
